import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Layout from '../../Layout/Layout';
import Swal from 'sweetalert2';
import { 
  FaSave, FaSpinner, FaExclamationTriangle, 
  FaCalendarAlt, FaTag, FaMapMarkerAlt, 
  FaFileUpload, FaUser, FaEnvelope, FaPhone,
  FaMoneyBillWave, FaExchangeAlt, FaPiggyBank,
  FaChartLine, FaArrowLeft, FaChevronDown,
  FaFilter, FaTimes, FaInfoCircle, FaCreditCard
} from 'react-icons/fa';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  Dropdown,
  Badge,
  Alert,
  FloatingLabel,
  ButtonGroup,
  Nav,
  Tab,
  Tabs,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import './CreateTransaction.css';

// API Configuration
const API_BASE_URL = 'http://localhost:8000/api';

// SweetAlert2 Configuration
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const CreateTransaction = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isPaymentMethodOpen, setIsPaymentMethodOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');

  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    category: null,
    payment_method: null,
    status: 'pending',
    sender_name: '',
    sender_email: '',
    sender_phone: '',
    receiver_name: '',
    receiver_email: '',
    receiver_phone: '',
  });

  const [errors, setErrors] = useState({});
  const [currentTag, setCurrentTag] = useState('');

  // Custom styles for React-Select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      border: `2px solid ${state.isFocused ? '#3b82f6' : '#e2e8f0'}`,
      borderRadius: '8px',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(59, 130, 246, 0.1)' : 'none',
      backgroundColor: 'white',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#eff6ff' 
        : state.isFocused 
          ? '#f8fafc' 
          : 'white',
      color: state.isSelected ? '#3b82f6' : '#1e293b',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#eff6ff'
      }
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      zIndex: 9999
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px',
      maxHeight: '200px'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px'
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    placeholder: (base) => ({
      ...base,
      color: '#94a3b8'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1e293b'
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: 'none'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#64748b',
      padding: '0 8px',
      '&:hover': {
        color: '#3b82f6'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#64748b',
      padding: '0 8px',
      '&:hover': {
        color: '#8d0909ff'
      }
    })
  };

  // Dark mode styles
  const darkSelectStyles = {
    ...customSelectStyles,
    control: (base, state) => ({
      ...customSelectStyles.control(base, state),
      backgroundColor: '#1e293b',
      borderColor: state.isFocused ? '#60a5fa' : '#334155',
      '&:hover': {
        borderColor: state.isFocused ? '#60a5fa' : '#475569'
      }
    }),
    menu: (base) => ({
      ...customSelectStyles.menu(base),
      backgroundColor: '#1e293b',
      border: '1px solid #334155'
    }),
    option: (base, state) => ({
      ...customSelectStyles.option(base, state),
      backgroundColor: state.isSelected 
        ? '#1e3a8a' 
        : state.isFocused 
          ? '#0f172a' 
          : '#1e293b',
      color: state.isSelected ? '#60a5fa' : '#f1f5f9',
      '&:active': {
        backgroundColor: '#1e3a8a'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: '#f1f5f9'
    }),
    input: (base) => ({
      ...base,
      color: '#f1f5f9'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#94a3b8'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#94a3b8',
      '&:hover': {
        color: '#60a5fa'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#94a3b8',
      '&:hover': {
        color: '#f87171'
      }
    })
  };

  // Format categories for React-Select
  const categoryOptions = React.useMemo(() => 
    categories.map(category => ({
      value: category.id,
      label: category.name
    })), [categories]);

  useEffect(() => {
    fetchCategories();
    fetchPaymentMethods();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Authentication token not found');

      const response = await fetch(`${API_BASE_URL}/categories/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      console.log(response);

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch categories');

      // Sort categories by name
      const sortedCategories = data.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(sortedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error.message === 'Session expired') {
        setTokenExpired(true);
        showSessionExpiredAlert();
      } else {
        showErrorAlert('Failed to load categories');
      }
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Authentication token not found');

      const response = await fetch(`${API_BASE_URL}/payment-methods/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      console.log(response);

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch payment methods');

      setPaymentMethods(data);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      if (error.message === 'Session expired') {
        setTokenExpired(true);
        showSessionExpiredAlert();
      } else {
        showErrorAlert('Failed to load payment methods');
      }
    }
  };

  // Update category selection handling
  const handleCategorySelect = (selectedOption) => {
    const category = categories.find(cat => cat.id === selectedOption?.value);
    setSelectedCategory(category);
    setFormData(prev => ({ 
      ...prev, 
      category: selectedOption?.value || null
    }));
    
    // Clear category error if exists
    if (errors.category) {
      setErrors(prev => ({ ...prev, category: '' }));
    }
  };

  // Update payment method selection handling
  const handlePaymentMethodSelect = (e) => {
    const methodId = e.target.value;
    const method = paymentMethods.find(m => m.id === parseInt(methodId));
    setSelectedPaymentMethod(method);
    setFormData(prev => ({ 
      ...prev, 
      payment_method: methodId 
    }));
    
    // Clear payment method error if exists
    if (errors.payment_method) {
      setErrors(prev => ({ ...prev, payment_method: '' }));
    }
  };

  // Filter categories based on search
  const filteredCategories = React.useMemo(() => {
    return categories.filter(category =>
      category.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
  }, [categories, categorySearch]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showErrorAlert('Please fill in all required fields correctly');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Authentication token not found');

      const formDataToSend = new FormData();
      
      // Append all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (key === 'tags') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/transactions/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formDataToSend
      });

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create transaction');
      }

      showSuccessAlert('Transaction created successfully');
      navigate('/transactions');
    } catch (error) {
      console.error('Error creating transaction:', error);
      if (error.message === 'Session expired') {
        setTokenExpired(true);
        showSessionExpiredAlert();
      } else {
        showErrorAlert(error.message || 'Failed to create transaction');
      }
    } finally {
      setLoading(false);
    }
  };

  const showSuccessAlert = (message) => {
    Toast.fire({
      icon: 'success',
      title: message
    });
  };

  const showErrorAlert = (message) => {
    Toast.fire({
      icon: 'error',
      title: message
    });
  };

  const showSessionExpiredAlert = () => {
    Swal.fire({
      title: 'Session Expired',
      text: 'Your session has expired. Please log in again.',
      icon: 'warning',
      confirmButtonColor: '#3b82f6',
      confirmButtonText: 'Go to Login',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/login');
      }
    });
  };

  return (
    <Layout>
      <div className="create-transaction-container">
        <div className="page-header">
          <div className="header-content">
            <h1>Create Transaction</h1>
            <p>Record a new financial transaction with detailed information</p>
          </div>
          <Button 
            style={{backgroundColor: '#103166'}}
            variant="outline-primary"
            // className="d-flex align-items-center gap-2"
            onClick={() => navigate('/transactions')}
          >
            <FaArrowLeft /> Back to Transactions
          </Button>
        </div>

        <div className="transaction-form">
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-section">
                <h2>
                  <FaMoneyBillWave className="text-primary" /> Transaction Details
                </h2>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="amount">
                      Amount <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        required
                        className={errors.amount ? 'error' : ''}
                      />
                    </div>
                    {errors.amount && <span className="error-message">{errors.amount}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="date">
                      Date <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className={errors.date ? 'error' : ''}
                      />
                    </div>
                    {errors.date && <span className="error-message">{errors.date}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">
                    Description <span className="required">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter transaction description"
                    rows="3"
                    required
                    className={errors.description ? 'error' : ''}
                  />
                  {errors.description && <span className="error-message">{errors.description}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">
                      Category <span className="required">*</span>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Select a category for this transaction</Tooltip>}
                      >
                        <FaInfoCircle className="text-muted ms-1" size={14} />
                      </OverlayTrigger>
                    </label>
                    <Select
                      id="category"
                      name="category"
                      value={categoryOptions.find(option => option.value === formData.category) || null}
                      onChange={handleCategorySelect}
                      options={categoryOptions}
                      placeholder="Select a category"
                      isClearable
                      isSearchable
                      styles={window.matchMedia('(prefers-color-scheme: dark)').matches ? darkSelectStyles : customSelectStyles}
                      className={errors.category ? 'error' : ''}
                      classNamePrefix="select"
                      noOptionsMessage={() => "No categories found"}
                      loadingMessage={() => "Loading categories..."}
                      aria-label="Select a category"
                    />
                    {errors.category && <span className="error-message">{errors.category}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="payment_method">
                      Payment Method <span className="required">*</span>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Select the payment method for this transaction</Tooltip>}
                      >
                        <FaInfoCircle className="text-muted ms-1" size={14} />
                      </OverlayTrigger>
                    </label>
                    <div className="select-wrapper">
                      <select
                        id="payment_method"
                        name="payment_method"
                        value={formData.payment_method || ''}
                        onChange={handlePaymentMethodSelect}
                        className={`form-select ${errors.payment_method ? 'error' : ''}`}
                        required
                      >
                        <option value="">Select a payment method</option>
                        {paymentMethods.map(method => (
                          <option key={method.id} value={method.id}>
                            {method.name}
                          </option>
                        ))}
                      </select>
                      <FaChevronDown className="select-icon" />
                    </div>
                    {errors.payment_method && <span className="error-message">{errors.payment_method}</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/transactions')}
                className="btn-secondary"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Create Transaction
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateTransaction;
